/**
 * MercatoPage — lista operazioni mercato (FASE 3 iter 6).
 *
 * - Filtri: squadra (POV), stato (7 stati)
 * - Lista compatta con tipo, controparti, importi, stato
 * - Dettaglio modale: thread chain + azioni accetta/rifiuta/annulla/controfferta
 * - CTA "Nuova operazione" → MercatoWizard
 *
 * Bridge v1.4 Sez. 1.12 + App. D.
 */

import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../hooks/useAuth";
import { useStagioneAttiva } from "../../hooks/useStagione";
import { useMieSquadreGestite, useSquadre } from "../../hooks/useFase7";
import { useAuthStore } from "../../lib/auth-store";
import { api } from "../../lib/api";

interface FinestraItem {
  id: string;
  stagione_id: string;
  tipo: "estivo" | "invernale" | "asta_svincolati";
  attiva: boolean;
  data_apertura: string;
  data_chiusura: string;
}

const MERCATO_ADMIN_ROLES = new Set(["presidente_lega", "admin", "mod_mercato"]);

function userIsMercatoAdmin(user: ReturnType<typeof useAuth>["user"]): boolean {
  if (!user) return false;
  const roles = new Set<string>([user.ruolo_lega ?? ""]);
  for (const r of user.ruoli_aggiuntivi ?? []) roles.add(r);
  for (const r of roles) if (MERCATO_ADMIN_ROLES.has(r)) return true;
  return false;
}
import {
  OperazioneItem,
  StatoOperazione,
  TipoOperazione,
  metaPerTipo,
  useAccettaOperazione,
  useAnnullaOperazione,
  useListaOperazioni,
  useRifiutaOperazione,
  useThreadOperazione,
} from "../../hooks/useFase3";
import { MercatoWizard } from "./MercatoWizard";
import { MobileShell } from "../shell/MobileShell";
import { localeCorrente } from "../../i18n";
import { tr } from "../../i18n";

const STATO_COLOR: Record<StatoOperazione, string> = {
  proposta: "bg-warning/20 text-warning",
  controproposta_emessa: "bg-info/20 text-info",
  accettata: "bg-success/20 text-success",
  rifiutata: "bg-danger/20 text-danger",
  annullata: "bg-fg-muted/20 text-fg-muted",
  auto_rifiutata_budget: "bg-danger/30 text-danger",
  auto_rifiutata_giocatore: "bg-danger/30 text-danger",
};

const STATO_LABEL: Record<StatoOperazione, string> = {
  proposta: tr("in attesa"),
  controproposta_emessa: "controproposta",
  accettata: "accettata",
  rifiutata: "rifiutata",
  annullata: "annullata",
  auto_rifiutata_budget: tr("auto-rifiuto budget"),
  auto_rifiutata_giocatore: tr("auto-rifiuto giocatore"),
};

/** Andrea 2026-05-23: nei filtri visibili teniamo solo gli stati
 * "operativi" — niente auto-rifiuti (eventi tecnici, l'utente non li
 * filtra mai). I record con quegli stati restano nel DB ma non hanno un
 * bottone-filtro. Andrea 2026-09-10: "controproposta" e' tornata tra i
 * filtri, ora che si puo' fare la controfferta dal dettaglio. */
const TUTTI_STATI: StatoOperazione[] = [
  "proposta",
  "controproposta_emessa",
  "accettata",
  "rifiutata",
  "annullata",
];

function fmtImporto(s: string | null): string {
  if (!s) return "—";
  const n = Number(s);
  return Number.isFinite(n) ? `${n.toFixed(2)}M` : s;
}

/** Nome squadra sempre leggibile: mai un pezzo di uuid in faccia
 * all'utente (Andrea 2026-09-10). I nomi arrivano pre-joinati dal
 * backend, il fallback e' solo per dati incompleti. */
function nomeSquadraSicuro(nome: string | null | undefined): string {
  return nome && nome.trim() ? nome : tr("squadra sconosciuta");
}

/** Data breve: "10/09, 22:14". */
function fmtData(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(localeCorrente(), {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** L'affare in una riga: giocatori e cifre, niente id. */
function riassuntoOperazione(op: OperazioneItem): string {
  const pezzi: string[] = [];
  const nomi = op.giocatori.map((g) => g.nome).filter((x): x is string => !!x);
  if (nomi.length > 0) pezzi.push(nomi.join(" + "));
  else if (op.giocatori.length > 0) pezzi.push(tr("{{v1}} giocatori", { v1: op.giocatori.length }));
  if (op.importo_offerto_milioni) pezzi.push(fmtImporto(op.importo_offerto_milioni));
  if (op.conguaglio_milioni)
    pezzi.push(tr("conguaglio {{v1}}", { v1: fmtImporto(op.conguaglio_milioni) }));
  if (op.importo_riscatto_milioni)
    pezzi.push(tr("riscatto {{v1}}", { v1: fmtImporto(op.importo_riscatto_milioni) }));
  return pezzi.join(" · ") || "dettagli non visibili";
}

/** Stato letto dentro la storia: una proposta a cui è stata data una
 * controfferta, per chi legge, e' semplicemente stata superata. */
const STATO_STORIA: Record<StatoOperazione, string> = {
  ...STATO_LABEL,
  controproposta_emessa: tr("superata da controfferta"),
};

/** Storia della trattativa: chi ha proposto cosa, in ordine. Sostituisce
 * il vecchio elenco tecnico "root: #uuid… ↳ #uuid…" (Andrea 2026-09-10). */
function StoriaTrattativa({
  passi,
  opCorrenteId,
}: {
  passi: OperazioneItem[];
  opCorrenteId: string;
}) {
  return (
    <div>
      <div className="text-xs text-fg-muted uppercase tracking-wider mb-2">
        Come è andata ({passi.length} passaggi)
      </div>
      <ol className="space-y-2">
        {passi.map((p, i) => {
          const meta = metaPerTipo(p.tipo as TipoOperazione);
          const corrente = p.id === opCorrenteId;
          return (
            <li
              key={p.id}
              className={`flex gap-2 rounded-lg border p-2 ${
                corrente ? "border-accent/50 bg-accent/5" : "border-primary-soft bg-primary-deep/30"
              }`}
            >
              <span className="shrink-0 w-5 h-5 rounded-full bg-primary-deep border border-primary-soft text-[10px] font-mono text-fg-muted flex items-center justify-center">
                {i + 1}
              </span>
              <div className="min-w-0 flex-1 text-xs">
                <div className="flex flex-wrap items-baseline gap-x-1.5">
                  <span className="font-bold text-fg">
                    {nomeSquadraSicuro(p.nome_squadra_proponente)}
                  </span>
                  <span className="text-fg-muted">
                    {i === 0 ? tr("ha proposto") : tr("ha controfferto")}
                  </span>
                  {corrente && (
                    <span className="text-[10px] text-accent uppercase tracking-wider">
                      {tr("· questa")}
                    </span>
                  )}
                </div>
                <div className="text-fg mt-0.5">
                  {meta.emoji} {meta.label} · {riassuntoOperazione(p)}
                </div>
                {p.messaggio_proponente && (
                  <div className="text-fg-muted italic mt-0.5">“{p.messaggio_proponente}”</div>
                )}
                <div className="flex items-center gap-2 mt-1">
                  <span
                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${STATO_COLOR[p.stato]}`}
                  >
                    {STATO_STORIA[p.stato]}
                  </span>
                  <span className="text-[10px] text-fg-muted font-mono">
                    {fmtData(p.data_proposta)}
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

interface RigaOpProps {
  op: OperazioneItem;
  squadraPov: string;
  onOpen: () => void;
}

function RigaOp({ op, squadraPov, onOpen }: RigaOpProps) {
  const meta = metaPerTipo(op.tipo as TipoOperazione);
  const sonoProponente = op.squadra_proponente_id === squadraPov;
  const controparteNome = sonoProponente
    ? nomeSquadraSicuro(op.nome_squadra_destinataria)
    : nomeSquadraSicuro(op.nome_squadra_proponente);

  return (
    <button
      onClick={onOpen}
      className="w-full text-left flex items-center gap-3 p-4 border-b border-primary/10 last:border-b-0 hover:bg-primary-deep/40 transition-colors"
    >
      <span className="text-2xl shrink-0">{meta.emoji}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-bold">{meta.label}</span>
          <span className="text-xs text-fg-muted">
            {sonoProponente ? tr("→ verso") : "← da"}{" "}
            <span className="font-bold text-fg">{controparteNome}</span>
          </span>
        </div>
        <div className="text-xs text-fg-muted flex flex-wrap gap-x-3 gap-y-1 mt-1">
          <span>{op.giocatori.length} giocatori</span>
          {op.importo_offerto_milioni && (
            <span>offerta {fmtImporto(op.importo_offerto_milioni)}</span>
          )}
          {op.conguaglio_milioni && <span>conguaglio {fmtImporto(op.conguaglio_milioni)}</span>}
          {op.importo_riscatto_milioni && (
            <span>riscatto {fmtImporto(op.importo_riscatto_milioni)}</span>
          )}
          <span>
            {new Date(op.data_proposta).toLocaleString(localeCorrente(), {
              dateStyle: "short",
              timeStyle: "short",
            })}
          </span>
        </div>
      </div>
      <span className={`pill ${STATO_COLOR[op.stato]}`}>{STATO_LABEL[op.stato]}</span>
    </button>
  );
}

interface DetailProps {
  op: OperazioneItem;
  squadraPov: string;
  /** Squadre che l'utente GESTISCE davvero (presidente o allenatore in
   * seconda). Andrea 2026-09-06: i bottoni azione si basano su queste,
   * NON sul POV del filtro — cambiare POV non deve permettere di
   * accettare/rifiutare al posto dell'altra squadra. Andrea 2026-09-07:
   * vale anche per gli admin — le trattative sono delle due squadre. */
  gestiteIds: string[];
  stagioneId: string;
  /** Andrea 2026-05-23: se true, disabilita Accetta/Rifiuta perche'
   * c'e' una giornata in lavorazione (deadline passata o voti caricati). */
  giornataInCorso: boolean;
  /** Apre il wizard in modalita' controfferta su questa operazione. */
  onControfferta: (op: OperazioneItem) => void;
  onClose: () => void;
}

function MercatoDetail({
  op,
  squadraPov,
  gestiteIds,
  stagioneId,
  giornataInCorso,
  onControfferta,
  onClose,
}: DetailProps) {
  const meta = metaPerTipo(op.tipo as TipoOperazione);
  const { data: thread } = useThreadOperazione(op.id);
  const accettaMut = useAccettaOperazione();
  const rifiutaMut = useRifiutaOperazione();
  const annullaMut = useAnnullaOperazione();
  const [actionError, setActionError] = useState<string | null>(null);

  const sonoProponente = gestiteIds.includes(op.squadra_proponente_id);
  const sonoDestinatario = gestiteIds.includes(op.squadra_destinataria_id);
  void squadraPov; // POV usato solo per l'evidenza visiva nelle righe lista
  const isPendente = op.stato === "proposta";

  async function handleAccetta() {
    if (!stagioneId.trim()) {
      setActionError(tr("Manca la stagione_id (impostala sulla pagina)."));
      return;
    }
    setActionError(null);
    try {
      await accettaMut.mutateAsync({ opId: op.id, stagioneId: stagioneId.trim() });
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setActionError(err.response?.data?.detail ?? err.message ?? "errore");
    }
  }

  async function handleRifiuta() {
    setActionError(null);
    try {
      await rifiutaMut.mutateAsync(op.id);
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setActionError(err.response?.data?.detail ?? err.message ?? "errore");
    }
  }

  async function handleAnnulla() {
    setActionError(null);
    try {
      await annullaMut.mutateAsync(op.id);
      onClose();
    } catch (e) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setActionError(err.response?.data?.detail ?? err.message ?? "errore");
    }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-bg border border-accent/30 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="bg-primary-deep px-6 py-4 border-b border-accent/30 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3 min-w-0">
            <span className="text-2xl">{meta.emoji}</span>
            <div className="min-w-0">
              <h3 className="font-display text-xl text-accent tracking-wide truncate">
                {meta.label}
              </h3>
              {/* Andrea 2026-09-10: qui c'era il pezzo di uuid. */}
              <p className="text-xs text-fg-muted truncate">
                {op.operazione_padre_id ? tr("Controfferta · ") : ""}
                proposta il {fmtData(op.data_proposta)}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="btn-ghost text-sm">
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Andrea 2026-09-07: giocatori incolonnati SOTTO la squadra che
              li cede — "esce"/"entra" relativi al proponente confondevano
              chi guardava dal lato del destinatario. Così la lettura è
              corretta da qualunque punto di vista. */}
          <div className="grid grid-cols-2 gap-3 text-sm">
            {(
              [
                {
                  ruolo: tr("Proponente"),
                  nome: nomeSquadraSicuro(op.nome_squadra_proponente),
                  tu: sonoProponente,
                  // il proponente cede i giocatori con direzione "esce"
                  cede: op.giocatori.filter((g) => g.direzione === "esce"),
                },
                {
                  ruolo: tr("Destinatario"),
                  nome: nomeSquadraSicuro(op.nome_squadra_destinataria),
                  tu: sonoDestinatario,
                  // "entra" (al proponente) = lo cede il destinatario
                  cede: op.giocatori.filter((g) => g.direzione === "entra"),
                },
              ] as const
            ).map((lato) => (
              <div
                key={lato.ruolo}
                className="bg-primary-deep/40 border border-primary-soft rounded-lg p-3"
              >
                <div className="text-[10px] text-fg-muted uppercase tracking-wider">
                  {lato.ruolo}
                </div>
                <div className="font-bold truncate">
                  {lato.nome} {lato.tu && <span className="text-accent">{tr("(tu)")}</span>}
                </div>
                <div className="text-[10px] text-warning uppercase tracking-wider mt-2 mb-1">
                  {tr("Cede")}
                </div>
                {lato.cede.length === 0 ? (
                  <div className="text-xs text-fg-muted">{tr("— nessuno")}</div>
                ) : (
                  <div className="space-y-1">
                    {lato.cede.map((g) => (
                      <div key={g.giocatore_id} className="text-sm leading-tight">
                        <span className="font-bold">{g.nome ?? "giocatore"}</span>
                        {g.club_serie_a && (
                          <span className="text-xs text-fg-muted"> · {g.club_serie_a}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div>
            <div className="text-xs text-fg-muted uppercase tracking-wider mb-2">{tr("Stato")}</div>
            <span className={`pill ${STATO_COLOR[op.stato]}`}>{STATO_LABEL[op.stato]}</span>
          </div>

          <div className="grid grid-cols-2 gap-3 text-sm">
            {op.importo_offerto_milioni && (
              <div>
                <div className="text-xs text-fg-muted uppercase tracking-wider">
                  {tr("Offerta")}
                </div>
                <div className="font-mono text-accent">
                  {fmtImporto(op.importo_offerto_milioni)}
                </div>
              </div>
            )}
            {op.conguaglio_milioni && (
              <div>
                <div className="text-xs text-fg-muted uppercase tracking-wider">
                  {tr("Conguaglio")}
                </div>
                <div className="font-mono text-accent">{fmtImporto(op.conguaglio_milioni)}</div>
              </div>
            )}
            {op.importo_riscatto_milioni && (
              <div>
                <div className="text-xs text-fg-muted uppercase tracking-wider">
                  {tr("Riscatto")}
                </div>
                <div className="font-mono text-accent">
                  {fmtImporto(op.importo_riscatto_milioni)}
                </div>
              </div>
            )}
            {op.data_scadenza && (
              <div>
                <div className="text-xs text-fg-muted uppercase tracking-wider">
                  {tr("Scadenza")}
                </div>
                <div className="text-fg-muted">
                  {new Date(op.data_scadenza).toLocaleString(localeCorrente())}
                </div>
              </div>
            )}
          </div>

          {op.condizione_riscatto_testo && (
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wider">
                {tr("Condizione riscatto")}
              </div>
              <div className="text-sm bg-primary-deep/40 rounded-lg p-2 mt-1">
                {op.condizione_riscatto_testo}
              </div>
            </div>
          )}

          {op.messaggio_proponente && (
            <div>
              <div className="text-xs text-fg-muted uppercase tracking-wider">
                {tr("Messaggio")}
              </div>
              <div className="text-sm bg-primary-deep/40 rounded-lg p-2 mt-1">
                {op.messaggio_proponente}
              </div>
            </div>
          )}

          {thread && thread.catena.length > 0 && (
            <StoriaTrattativa passi={[thread.root, ...thread.catena]} opCorrenteId={op.id} />
          )}

          {actionError && (
            <div className="text-sm text-danger bg-danger/10 rounded-lg p-3">{actionError}</div>
          )}
        </div>

        <div className="bg-primary-deep/40 px-6 py-4 border-t border-primary-soft flex items-center justify-between sticky bottom-0">
          <button onClick={onClose} className="btn-ghost text-sm">
            {tr("Chiudi")}
          </button>
          {isPendente && (
            <div className="flex gap-2 flex-wrap justify-end items-center">
              {giornataInCorso && sonoDestinatario && (
                <span className="text-[10px] text-info uppercase tracking-wider font-mono mr-2">
                  {tr("⏸ Giornata in corso")}
                </span>
              )}
              {sonoProponente && (
                <button
                  onClick={handleAnnulla}
                  disabled={annullaMut.isPending}
                  className="btn-ghost text-sm text-fg-muted disabled:opacity-50"
                >
                  {annullaMut.isPending ? "…" : tr("Annulla")}
                </button>
              )}
              {sonoDestinatario && (
                <>
                  {/* Andrea 2026-09-10: invece di accettare o rifiutare si
                      puo' rispondere con una controfferta. */}
                  <button
                    onClick={() => onControfferta(op)}
                    disabled={giornataInCorso}
                    title={
                      giornataInCorso
                        ? tr("Giornata in corso: puoi rispondere dopo il calcolo")
                        : tr("Rispondi con una tua controfferta")
                    }
                    className="btn-ghost text-sm text-info disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {tr("↩ Controfferta")}
                  </button>
                  <button
                    onClick={handleRifiuta}
                    disabled={rifiutaMut.isPending || giornataInCorso}
                    title={
                      giornataInCorso
                        ? tr("Giornata in corso: puoi rifiutare solo dopo il calcolo")
                        : ""
                    }
                    className="btn-ghost text-sm text-danger disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {rifiutaMut.isPending ? "…" : tr("Rifiuta")}
                  </button>
                  <button
                    onClick={handleAccetta}
                    disabled={accettaMut.isPending || !stagioneId.trim() || giornataInCorso}
                    title={
                      giornataInCorso
                        ? tr("Giornata in corso: puoi accettare solo dopo il calcolo")
                        : ""
                    }
                    className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {accettaMut.isPending ? "…" : tr("Accetta")}
                  </button>
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function MercatoPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isAuth = !!useAuthStore((s) => s.accessToken);
  const { data: stagioneAttiva } = useStagioneAttiva();
  const stagioneId = stagioneAttiva?.id ?? "";

  // Auto squadra: la propria (presidente OR allenatore in seconda).
  // Andrea 2026-05-21: include anche le squadre dove sono socio.
  const { data: squadre } = useSquadre();
  const { data: idsGestite } = useMieSquadreGestite();
  const miaSquadra = squadre?.find(
    (s) => s.presidente_user_id === user?.id || (idsGestite ?? []).includes(s.id),
  );
  const miaSquadraId = miaSquadra?.id ?? "";
  // Tutte le squadre che gestisco (presidente o secondo): base per i
  // bottoni azione nel dettaglio, indipendente dal POV del filtro.
  const gestiteIds = [
    ...new Set([
      ...(idsGestite ?? []),
      ...(squadre ?? []).filter((s) => s.presidente_user_id === user?.id).map((s) => s.id),
    ]),
  ];

  // POV operazioni: di default le proprie; admin/mod_mercato può anche
  // scegliere "Tutte" o un'altra squadra. Andrea 2026-05-19: serve un
  // filtro esplicito per capire da chi vengono le operazioni mostrate.
  const isAdminMercatoEarly = userIsMercatoAdmin(user);
  const [povSquadraId, setPovSquadraId] = useState<string>("");
  // Inizializza alla propria squadra appena disponibile.
  useEffect(() => {
    if (povSquadraId) return;
    if (miaSquadraId) setPovSquadraId(miaSquadraId);
  }, [miaSquadraId, povSquadraId]);

  // Sentinella speciale per "tutte le squadre" (admin only).
  const POV_TUTTE = "__TUTTE__";
  // `squadraId` è quello effettivamente usato in tutti i path che richiedono
  // una squadra concreta (wizard, RigaOp, MercatoDetail). Quando POV è
  // "Tutte" usiamo `miaSquadraId` come riferimento POV per l'evidenza
  // "tua/altra" ma la lista non filtra per squadra.
  const squadraId = povSquadraId === POV_TUTTE ? miaSquadraId : povSquadraId;

  // Finestre mercato della stagione → identifica quella attiva
  const { data: finestre } = useQuery({
    queryKey: ["stagione-finestre", stagioneId],
    queryFn: async () =>
      (await api.get<FinestraItem[]>(`/admin/stagioni/${stagioneId}/finestre`)).data,
    enabled: isAuth && !!stagioneId,
  });
  const finestraAttiva = finestre?.find((f) => f.attiva) ?? null;
  const finestraEsistente = finestraAttiva ?? finestre?.[finestre.length - 1] ?? null;
  const mercatoAperto = !!finestraAttiva;
  const isAdminMercato = userIsMercatoAdmin(user);

  // Andrea 2026-05-23: giornate stagione attiva → blocca accetta/rifiuta
  // operazioni quando una giornata e' "in lavorazione" (voti caricati /
  // annullata / da_giocare con deadline passata). Le proposte si possono
  // comunque creare; solo le risposte vengono congelate.
  const { data: giornate } = useQuery({
    queryKey: ["stagione-giornate-mercato", stagioneId],
    queryFn: async () =>
      (
        await api.get<
          {
            id: string;
            stato: string;
            data_inizio_partite_sa: string | null;
          }[]
        >(`/admin/stagioni/${stagioneId}/giornate`)
      ).data,
    enabled: isAuth && !!stagioneId,
  });
  const giornataInCorso = (() => {
    if (!giornate) return false;
    const now = Date.now();
    return giornate.some((g) => {
      if (g.stato === "voti_caricati" || g.stato === "annullata") return true;
      if (
        g.stato === "da_giocare" &&
        g.data_inizio_partite_sa &&
        new Date(g.data_inizio_partite_sa).getTime() <= now
      ) {
        return true;
      }
      return false;
    });
  })();

  // Mutations per gestione finestra (admin/mod_mercato)
  const creaFinestraMut = useMutation({
    mutationFn: async () => {
      await api.post("/admin/finestre", {
        stagione_id: stagioneId,
        tipo: "estivo",
        attiva: true,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stagione-finestre", stagioneId] }),
  });
  const apriFinestraMut = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/admin/finestre/${id}/apri`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["stagione-finestre", stagioneId] }),
  });
  const chiudiFinestraMut = useMutation({
    mutationFn: async (id: string) => {
      await api.post(`/admin/finestre/${id}/chiudi`);
    },
    onSuccess: () => {
      // Andrea 2026-05-21: alla chiusura del mercato il backend
      // auto-finalizza la lista 25 (promuove provvisori in ordine
      // alfabetico fino a 25) e setta rose_finalizzate_at. Dobbiamo
      // invalidare anche rosa e operazioni perche' la chiusura modifica
      // sia la rosa visualizzata che le proposte mercato pendenti
      // (annullate automaticamente).
      qc.invalidateQueries({ queryKey: ["stagione-finestre", stagioneId] });
      qc.invalidateQueries({ queryKey: ["rosa"] });
      qc.invalidateQueries({ queryKey: ["operazioni"] });
    },
  });

  const [filtroStato, setFiltroStato] = useState<StatoOperazione | "">("");
  const [showWizard, setShowWizard] = useState(false);
  const [opAperta, setOpAperta] = useState<OperazioneItem | null>(null);
  /** Offerta a cui sto contropponendo: apre il wizard in modalita'
   * controfferta (Andrea 2026-09-10). */
  const [controfferta, setControfferta] = useState<OperazioneItem | null>(null);

  const { data, isLoading } = useListaOperazioni({
    // Se POV = "Tutte" passiamo null al backend → lista globale (filtrata
    // dalla privacy lato server, FASE 7 iter 2).
    squadraId: povSquadraId === POV_TUTTE ? null : squadraId || null,
    stato: filtroStato || null,
    limit: 100,
    enabled: !!povSquadraId,
  });

  // Deep-link: se l'URL ha `?op=<id>` (es. da una notifica), apre
  // automaticamente il dettaglio dell'operazione. Andrea 2026-05-19.
  const [searchParams, setSearchParams] = useSearchParams();
  const opIdFromUrl = searchParams.get("op");
  useEffect(() => {
    if (!opIdFromUrl) return;
    if (opAperta?.id === opIdFromUrl) return;
    const target = (data ?? []).find((o) => o.id === opIdFromUrl);
    if (target) setOpAperta(target);
  }, [opIdFromUrl, data, opAperta]);
  // Mantengo sincronizzato URL ↔ state: chiudendo il modal il param
  // viene rimosso, così tornando indietro non si riapre.
  useEffect(() => {
    if (!opAperta && opIdFromUrl) {
      const next = new URLSearchParams(searchParams);
      next.delete("op");
      setSearchParams(next, { replace: true });
    }
  }, [opAperta, opIdFromUrl, searchParams, setSearchParams]);

  const conteggi = (data ?? []).reduce<Record<StatoOperazione, number>>(
    (acc, op) => {
      acc[op.stato] = (acc[op.stato] ?? 0) + 1;
      return acc;
    },
    {
      proposta: 0,
      controproposta_emessa: 0,
      accettata: 0,
      rifiutata: 0,
      annullata: 0,
      auto_rifiutata_budget: 0,
      auto_rifiutata_giocatore: 0,
    },
  );

  return (
    <MobileShell logoSuffix="MERCATO">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="font-display text-3xl text-accent tracking-wide">{tr("Mercato")}</h2>
          <span
            className={`pill text-xs font-bold ${
              mercatoAperto ? "bg-success/20 text-success" : "bg-danger/20 text-danger"
            }`}
          >
            {mercatoAperto ? tr("🟢 Aperto") : tr("🔴 Chiuso")}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/giocatori" className="btn-ghost text-sm border border-info/40 text-info">
            {tr("📋 Catalogo giocatori")}
          </Link>
          <button
            onClick={() => setShowWizard(true)}
            disabled={!squadraId || !mercatoAperto}
            title={!mercatoAperto ? tr("Mercato chiuso — nessuna nuova operazione") : ""}
            className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {tr("+ Nuova operazione")}
          </button>
        </div>
      </div>

      {!stagioneAttiva && (
        <div className="card text-fg-muted text-center p-4">{tr("Nessuna stagione attiva.")}</div>
      )}

      {/* Sezione admin: gestione finestra mercato */}
      {isAdminMercato && stagioneAttiva && (
        <div className="card flex items-center justify-between flex-wrap gap-2">
          <div className="text-sm">
            <div className="font-bold text-fg">{tr("Gestione mercato")}</div>
            <div className="text-xs text-fg-muted">
              {finestraEsistente
                ? finestraEsistente.attiva
                  ? tr("Stato: aperto")
                  : tr("Stato: chiuso")
                : tr("Mercato non ancora aperto per questa stagione.")}
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!finestraEsistente && (
              <button
                onClick={() => creaFinestraMut.mutate()}
                disabled={creaFinestraMut.isPending}
                className="btn-primary text-sm border border-success/40 text-success bg-success/10"
              >
                {tr("▶ Apri mercato")}
              </button>
            )}
            {finestraEsistente && !finestraEsistente.attiva && (
              <button
                onClick={() => apriFinestraMut.mutate(finestraEsistente.id)}
                disabled={apriFinestraMut.isPending}
                className="btn-primary text-sm border border-success/40 text-success bg-success/10"
              >
                {tr("▶ Apri mercato")}
              </button>
            )}
            {finestraEsistente && finestraEsistente.attiva && (
              <button
                onClick={() => chiudiFinestraMut.mutate(finestraEsistente.id)}
                disabled={chiudiFinestraMut.isPending}
                className="btn-primary text-sm border border-danger/40 text-danger bg-danger/10"
              >
                {tr("■ Chiudi mercato")}
              </button>
            )}
          </div>
        </div>
      )}

      {!mercatoAperto && squadraId && (
        <div className="card text-sm bg-warning/10 border-warning/30 text-warning">
          🔒 Mercato chiuso. Puoi consultare lo storico delle operazioni passate e il{" "}
          <Link to="/giocatori" className="underline">
            {tr("catalogo giocatori")}
          </Link>
          {tr(", ma non puoi creare nuove operazioni.")}
        </div>
      )}

      {giornataInCorso && squadraId && mercatoAperto && (
        <div className="card text-sm bg-info/10 border-info/30 text-info">
          ⏸️ <strong>{tr("Giornata in corso")}</strong> — Puoi creare nuove proposte, ma{" "}
          <strong>{tr("accettare/rifiutare")}</strong>{" "}
          {tr(
            tr(
              "operazioni esistenti e' bloccato finche' la giornata non viene calcolata. (Evita di perdere giocatori gia' schierati in formazione.)",
            ),
          )}
        </div>
      )}

      {/* Selettore POV: di default "le mie", admin/mod_mercato può
            anche scegliere altre squadre o "🌐 Tutte le squadre".
            Andrea 2026-05-19: filtro esplicito sulle trattative. */}
      {squadre && squadre.length > 0 && (
        <div className="card flex items-center gap-2 flex-wrap">
          <label className="text-xs uppercase tracking-wider text-fg-muted shrink-0">
            {tr("Operazioni di:")}
          </label>
          <select
            value={povSquadraId}
            onChange={(e) => setPovSquadraId(e.target.value)}
            className="flex-1 min-w-[200px] px-3 py-2 bg-primary-deep border border-primary-soft rounded-lg text-fg text-sm focus:outline-none focus:ring-2 focus:ring-accent"
          >
            {miaSquadraId && (
              <option value={miaSquadraId}>⚜ Le mie trattative ({miaSquadra?.nome})</option>
            )}
            {isAdminMercatoEarly && <option value={POV_TUTTE}>{tr("🌐 Tutte le squadre")}</option>}
            {squadre
              .filter((s) => s.id !== miaSquadraId)
              .map((s) => (
                <option key={s.id} value={s.id}>
                  {s.nome}
                </option>
              ))}
          </select>
        </div>
      )}

      {squadraId && (
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setFiltroStato("")}
            className={`pill ${filtroStato === "" ? "bg-accent text-ink" : "bg-primary-deep/40 text-fg-muted"}`}
          >
            tutti {data ? `(${data.length})` : ""}
          </button>
          {TUTTI_STATI.map((s) => (
            <button
              key={s}
              onClick={() => setFiltroStato(s)}
              className={`pill ${filtroStato === s ? STATO_COLOR[s].replace("/20", "/40") : "bg-primary-deep/40 text-fg-muted"}`}
            >
              {STATO_LABEL[s]} ({conteggi[s] ?? 0})
            </button>
          ))}
        </div>
      )}

      {!squadraId && (
        <div className="card text-fg-muted text-sm">
          {tr("Non sei presidente di nessuna squadra in questa lega.")}
        </div>
      )}

      {isLoading && squadraId && <div className="card text-fg-muted">{tr("Caricamento…")}</div>}

      {data && (
        <div className="card p-0 overflow-hidden">
          {data.length === 0 ? (
            <div className="p-6 text-center text-fg-muted">
              {tr("Nessuna operazione per i filtri correnti.")}
            </div>
          ) : (
            data.map((op) => (
              <RigaOp key={op.id} op={op} squadraPov={squadraId} onOpen={() => setOpAperta(op)} />
            ))
          )}
        </div>
      )}

      {showWizard && squadraId && (
        <MercatoWizard
          squadraProponenteId={squadraId.trim()}
          finestraIdDefault={finestraAttiva?.id}
          onClose={() => setShowWizard(false)}
        />
      )}

      {/* Controfferta: proponente = la MIA squadra, cioe' la destinataria
          dell'offerta ricevuta (non il POV del filtro). */}
      {controfferta && (
        <MercatoWizard
          squadraProponenteId={controfferta.squadra_destinataria_id}
          finestraIdDefault={controfferta.finestra_mercato_id}
          controproposta={controfferta}
          onClose={() => setControfferta(null)}
        />
      )}

      {opAperta && (
        <MercatoDetail
          op={opAperta}
          squadraPov={squadraId.trim()}
          gestiteIds={gestiteIds}
          stagioneId={stagioneId.trim()}
          giornataInCorso={giornataInCorso}
          onControfferta={(op) => {
            setOpAperta(null);
            setControfferta(op);
          }}
          onClose={() => setOpAperta(null)}
        />
      )}
    </MobileShell>
  );
}
